import { apiFetch } from "@/auth/apiClient";
import type { QuestionScenario } from "@/api/lms/exams";

export type MockTest = {
  id: string;
    position?: number;

  level: string;
  examType?: "JLPT" | "JFT_BASIC";
  title: string;
  duration: number;
  totalMarks: number;
  questionCount?: number;
  canManageQuestions?: boolean;
  isPublished?: boolean;
  sectionDurations?: string | null;
  passingMarks?: number;
  categoryPassMarks?: Record<string, number> | null;
  // Purchase / Access fields
  price?: number;
  currency?: string;
  attemptLimit?: number;
  bundleEligible?: boolean;
  accessDays?: number | null;
};

export type MockTestQuestion = {
  id: string;
    position?: number;

  mockTestId: string;
  scenarioId: string | null;
  scenario?: QuestionScenario | null;
  sortOrder?: number;
  type: "MCQ" | "WRITING" | "LISTENING";
  questionText: string;
  options: Record<string, unknown> | null;
  correctAnswer: string | null;
  marks: number;
  imageUrl?: string | null;
  audioUrl?: string | null;
  answerExplanation?: string | null;
};

export type MockTestAttempt = {
  id: string;
    position?: number;

  userId: number;
  mockTestId: string;
  score: string;
  attemptNumber: number;
  /**
   * Authoritative pass/fail locked at submission time.
   * Null for historical attempts (submitted before this field was added).
   */
  isPassed?: boolean | null;
  answers?: Record<string, unknown> | null;
  startedAt?: string;
  submittedAt?: string | null;
  endsAt?: string | null;
  lastSavedAt?: string | null;
};

/** Status of a student's access to a specific mock test */
export type MockTestAccessStatus = {
  status: "free" | "not_purchased" | "purchased" | "expired" | "refunded" | "subscribed";
  remainingAttempts: number;
  purchasedAttempts: number;
  /** True when purchased but all attempts are used up (buy more to continue). */
  depleted?: boolean;
  /** The active all-access subscription granting access, if any. */
  subscription?: MockTestSubscription | null;
  purchase: MockTestPurchase | null;
  mockTest: {
    price: number;
    currency: string;
    attemptLimit: number;
  };
};

// ── Subscription types ───────────────────────────────────────────────────────

/** An admin-managed all-access subscription plan. */
export type MockTestSubscriptionPlan = {
  id: string;
  title: string;
  description: string | null;
  durationMonths: number;
  price: number;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
};

export type MockTestSubscription = {
  id: string;
  userId: number;
  planId: string | null;
  planTitle: string | null;
  durationMonths: number;
  amountPaid: number;
  currency: string;
  paymentMethod: string | null;
  paymentRef: string | null;
  startedAt: string;
  expiresAt: string;
  status: "ACTIVE" | "EXPIRED" | "REFUNDED";
  refundedAt: string | null;
  createdAt: string;
  user?: { id: number; name?: string; email: string };
};

export type MockTestPurchase = {
  id: string;
    position?: number;

  userId: number;
  mockTestId: string;
  bundleId: string | null;
  purchasedAttempts: number;
  remainingAttempts: number;
  amountPaid: number;
  currency: string;
  paymentMethod: string | null;
  paymentRef: string | null;
  purchasedAt: string;
  expiresAt: string | null;
  mockTest?: MockTest;
  user?: { id: number; name?: string; email: string };
};

export type MockTestBundle = {
  id: string;
    position?: number;

  title: string;
  description: string | null;
  originalPrice: number;
  discountedPrice: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  items: Array<{
    id: string;
    position?: number;

    bundleId: string;
    mockTestId: string;
    mockTest: MockTest;
  }>;
};

export type MockTestsListResponse = {
  items: MockTest[];
  total: number;
  page: number;
  limit: number;
};

// ── Core Mock Test APIs ──────────────────────────────────────────────────────

export async function getMockTest(mockTestId: string) {
  return apiFetch<MockTest>(`/lms/mock-tests/${mockTestId}`, { method: "GET" });
}

export async function getMockTests(params: { page?: number; limit?: number } = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 100;
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiFetch<MockTestsListResponse>(`/lms/mock-tests?${qs.toString()}`, { method: "GET" });
}

export async function getAssignedMockTests(params: { page?: number; limit?: number } = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 100;
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiFetch<MockTestsListResponse>(`/lms/mock-tests/me/assigned?${qs.toString()}`, { method: "GET" });
}

export async function createMockTest(dto: {
  level?: string;
  examType?: "JLPT" | "JFT_BASIC";
  title: string;
  /** Omit to use server default (60) until set in the editor. */
  duration?: number;
  totalMarks: number;
  price?: number;
  attemptLimit?: number;
  passingMarks?: number;
}) {
  return apiFetch<MockTest>("/lms/mock-tests", { method: "POST", json: dto });
}

export async function updateMockTest(
  mockTestId: string,
  dto: {
    title?: string;
    duration?: number;
    level?: string;
    examType?: "JLPT" | "JFT_BASIC";
    isPublished?: boolean;
    sectionDurations?: string | null;
    passingMarks?: number;
    categoryPassMarks?: Record<string, number> | null;
    totalMarks?: number;
    price?: number;
    currency?: string;
    attemptLimit?: number;
    bundleEligible?: boolean;
    accessDays?: number | null;
  },
) {
  return apiFetch<MockTest>(`/lms/mock-tests/${mockTestId}`, { method: "PATCH", json: dto });
}

export async function deleteMockTest(mockTestId: string): Promise<void> {
  return apiFetch<void>(`/lms/mock-tests/${mockTestId}`, { method: "DELETE" });
}

export async function deleteMockTestQuestion(mockTestId: string, questionId: string): Promise<void> {
  return apiFetch<void>(`/lms/mock-tests/${mockTestId}/questions/${questionId}`, { method: "DELETE" });
}

export async function duplicateMockTest(mockTestId: string): Promise<MockTest> {
  return apiFetch<MockTest>(`/lms/mock-tests/${mockTestId}/duplicate`, { method: "POST" });
}

export async function patchMockTestQuestion(
  mockTestId: string,
  questionId: string,
  dto: {
    type?: "MCQ" | "WRITING" | "LISTENING";
    questionText?: string;
    options?: Record<string, unknown>;
    correctAnswer?: string | null;
    marks?: number;
    scenarioId?: string | null;
    sortOrder?: number;
    imageUrl?: string | null;
    audioUrl?: string | null;
    answerExplanation?: string | null;
  },
) {
  return apiFetch<MockTestQuestion>(`/lms/mock-tests/${mockTestId}/questions/${questionId}`, {
    method: "PATCH",
    json: dto,
  });
}

export async function assignMockTestTeacher(mockTestId: string, teacherId: number) {
  return apiFetch(`/lms/mock-tests/${mockTestId}/teachers`, {
    method: "POST",
    json: { teacherId },
  });
}

export async function getMockTestScenarios(mockTestId: string) {
  return apiFetch<QuestionScenario[]>(`/lms/mock-tests/${mockTestId}/scenarios`, {
    method: "GET",
  });
}

export async function createMockTestScenario(
  mockTestId: string,
  dto: {
    instructionsText?: string;
    imageUrl?: string;
    audioUrl?: string;
    sortOrder?: number;
  },
) {
  return apiFetch<QuestionScenario>(`/lms/mock-tests/${mockTestId}/scenarios`, {
    method: "POST",
    json: dto,
  });
}

export async function deleteMockTestScenario(mockTestId: string, scenarioId: string) {
  return apiFetch(`/lms/mock-tests/${mockTestId}/scenarios/${scenarioId}`, { method: "DELETE" });
}

export async function patchMockTestScenario(
  mockTestId: string,
  scenarioId: string,
  dto: {
    instructionsText?: string | null;
    imageUrl?: string | null;
    audioUrl?: string | null;
    sortOrder?: number;
  },
) {
  return apiFetch<QuestionScenario>(`/lms/mock-tests/${mockTestId}/scenarios/${scenarioId}`, {
    method: "PATCH",
    json: dto,
  });
}

export async function getMockTestQuestions(mockTestId: string) {
  return apiFetch<MockTestQuestion[]>(`/lms/mock-tests/${mockTestId}/questions`, {
    method: "GET",
  });
}

export async function startMockTest(mockTestId: string) {
  return apiFetch<MockTestAttempt>(`/lms/mock-tests/${mockTestId}/start`, {
    method: "POST",
  });
}

export async function getMockTestAttempt(attemptId: string) {
  return apiFetch<MockTestAttempt>(`/lms/mock-test-attempts/${attemptId}`, {
    method: "GET",
  });
}

export async function saveMockTestProgress(
  attemptId: string,
  body: { answers: Record<string, unknown> },
) {
  return apiFetch<MockTestAttempt>(`/lms/mock-test-attempts/${attemptId}/progress`, {
    method: "PATCH",
    json: body,
  });
}

export async function submitMockTest(attemptId: string, body: { answers: Record<string, unknown> }) {
  return apiFetch<MockTestAttempt>(`/lms/mock-test-attempts/${attemptId}/submit`, {
    method: "POST",
    json: body,
  });
}

export async function addMockTestQuestion(
  mockTestId: string,
  dto: {
    type: "MCQ" | "WRITING" | "LISTENING";
    questionText: string;
    options?: Record<string, unknown>;
    correctAnswer?: string;
    marks: number;
    scenarioId?: string;
    sortOrder?: number;
    imageUrl?: string;
    audioUrl?: string;
  },
) {
  return apiFetch<MockTestQuestion>(`/lms/mock-tests/${mockTestId}/questions`, {
    method: "POST",
    json: dto,
  });
}

// ── Purchase APIs ────────────────────────────────────────────────────────────

/** Get current user's access status for a specific mock test */
export async function getMockTestAccessStatus(mockTestId: string): Promise<MockTestAccessStatus> {
  return apiFetch<MockTestAccessStatus>(`/lms/mock-tests/${mockTestId}/access-status`, {
    method: "GET",
  });
}

/** Get all mock tests the current student has purchased */
export async function getMyMockTestPurchases(): Promise<MockTestPurchase[]> {
  return apiFetch<MockTestPurchase[]>(`/lms/mock-tests/me/purchases`, { method: "GET" });
}

/** Student: purchase a single mock test (works for free tests; for paid, payment must be processed first) */
export async function purchaseMockTest(
  mockTestId: string,
  dto: { paymentRef?: string; paymentMethod?: string; couponCode?: string; quantity?: number } = {},
): Promise<MockTestPurchase> {
  return apiFetch<MockTestPurchase>(`/lms/mock-tests/${mockTestId}/purchase`, {
    method: "POST",
    json: dto,
  });
}

// ── Cart (multi-test) APIs ───────────────────────────────────────────────────

export type CartItem = { mockTestId: string; quantity?: number };

export type CartQuote = {
  items: Array<{
    mockTestId: string;
    title: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
  total: number;
  currency: string;
};

/** Server-authoritative price quote for a multi-test cart. */
export async function quoteMockTestCart(items: CartItem[]): Promise<CartQuote> {
  return apiFetch<CartQuote>(`/lms/mock-tests/cart/quote`, {
    method: "POST",
    json: { items },
  });
}

/** Purchase several mock tests at once (free / already-paid path). */
export async function purchaseMockTestCart(
  items: CartItem[],
  dto: { paymentRef?: string; paymentMethod?: string } = {},
): Promise<MockTestPurchase[]> {
  return apiFetch<MockTestPurchase[]>(`/lms/mock-tests/cart/purchase`, {
    method: "POST",
    json: { items, ...dto },
  });
}

// ── Subscription plan catalogue + admin CRUD ─────────────────────────────────

/** Public: active all-access subscription plans. */
export async function getMockTestSubscriptionPlans(): Promise<MockTestSubscriptionPlan[]> {
  return apiFetch<MockTestSubscriptionPlan[]>(`/lms/mock-test-subscriptions/plans`, {
    method: "GET",
  });
}

/** Admin: all plans, including disabled ones. */
export async function getAllMockTestSubscriptionPlans(): Promise<MockTestSubscriptionPlan[]> {
  return apiFetch<MockTestSubscriptionPlan[]>(`/lms/mock-test-subscriptions/plans/all`, {
    method: "GET",
  });
}

/** Admin: create a plan. */
export async function createMockTestSubscriptionPlan(dto: {
  title: string;
  description?: string;
  durationMonths: number;
  price: number;
  currency?: string;
  isActive?: boolean;
  sortOrder?: number;
}): Promise<MockTestSubscriptionPlan> {
  return apiFetch<MockTestSubscriptionPlan>(`/lms/mock-test-subscriptions/plans`, {
    method: "POST",
    json: dto,
  });
}

/** Admin: update a plan. */
export async function updateMockTestSubscriptionPlan(
  planId: string,
  dto: {
    title?: string;
    description?: string;
    durationMonths?: number;
    price?: number;
    currency?: string;
    isActive?: boolean;
    sortOrder?: number;
  },
): Promise<MockTestSubscriptionPlan> {
  return apiFetch<MockTestSubscriptionPlan>(`/lms/mock-test-subscriptions/plans/${planId}`, {
    method: "PATCH",
    json: dto,
  });
}

/** Admin: delete a plan. */
export async function deleteMockTestSubscriptionPlan(planId: string): Promise<void> {
  return apiFetch<void>(`/lms/mock-test-subscriptions/plans/${planId}`, { method: "DELETE" });
}

// ── Subscription APIs ────────────────────────────────────────────────────────

/** Current user's active subscription (or null) plus history. */
export async function getMyMockTestSubscription(): Promise<{
  active: MockTestSubscription | null;
  history: MockTestSubscription[];
}> {
  return apiFetch(`/lms/mock-test-subscriptions/me`, { method: "GET" });
}

/** Student: subscribe/renew an all-access plan directly (free / admin path). */
export async function subscribeMockTestPlan(
  planId: string,
  dto: { paymentRef?: string; paymentMethod?: string } = {},
): Promise<MockTestSubscription> {
  return apiFetch<MockTestSubscription>(`/lms/mock-test-subscriptions/purchase`, {
    method: "POST",
    json: { planId, ...dto },
  });
}

/** Admin: grant a subscription to a student (no charge). */
export async function adminGrantMockTestSubscription(dto: {
  userId: number;
  planId: string;
  paymentRef?: string;
}): Promise<MockTestSubscription> {
  return apiFetch<MockTestSubscription>(`/lms/mock-test-subscriptions/grant`, {
    method: "POST",
    json: dto,
  });
}

/** Admin: list all subscriptions (paginated). */
export async function getAllMockTestSubscriptions(
  params: { page?: number; limit?: number } = {},
): Promise<{ items: MockTestSubscription[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams({
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 50),
  });
  return apiFetch(`/lms/mock-test-subscriptions?${qs}`, { method: "GET" });
}

/** Admin: manually grant access to a student */
export async function adminGrantMockTestAccess(
  mockTestId: string,
  dto: { userId: number; attempts?: number; paymentRef?: string },
): Promise<MockTestPurchase> {
  return apiFetch<MockTestPurchase>(`/lms/mock-tests/${mockTestId}/grant-access`, {
    method: "POST",
    json: dto,
  });
}

/** Admin: get all purchases for a specific test */
export async function getMockTestPurchases(mockTestId: string): Promise<MockTestPurchase[]> {
  return apiFetch<MockTestPurchase[]>(`/lms/mock-tests/${mockTestId}/purchases`, { method: "GET" });
}

/** Admin: get all purchases across all tests */
export async function getAllMockTestPurchases(params: { page?: number; limit?: number } = {}): Promise<{ items: MockTestPurchase[]; total: number }> {
  const qs = new URLSearchParams({
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 50),
  });
  return apiFetch(`/lms/mock-tests/admin/purchases?${qs}`, { method: "GET" });
}

/** Admin: revoke a purchase */
export async function revokeMockTestPurchase(purchaseId: string): Promise<void> {
  return apiFetch<void>(`/lms/mock-tests/admin/purchases/${purchaseId}`, { method: "DELETE" });
}

// ── Bundle APIs ──────────────────────────────────────────────────────────────

export async function getMockTestBundles(activeOnly = true): Promise<MockTestBundle[]> {
  const qs = activeOnly ? "" : "?all=1";
  return apiFetch<MockTestBundle[]>(`/lms/mock-test-bundles${qs}`, { method: "GET" });
}

export async function getMockTestBundle(bundleId: string): Promise<MockTestBundle> {
  return apiFetch<MockTestBundle>(`/lms/mock-test-bundles/${bundleId}`, { method: "GET" });
}

export async function createMockTestBundle(dto: {
  title: string;
  description?: string;
  originalPrice: number;
  discountedPrice: number;
  currency?: string;
  mockTestIds: string[];
}): Promise<MockTestBundle> {
  return apiFetch<MockTestBundle>(`/lms/mock-test-bundles`, { method: "POST", json: dto });
}

export async function updateMockTestBundle(
  bundleId: string,
  dto: {
    title?: string;
    description?: string;
    originalPrice?: number;
    discountedPrice?: number;
    currency?: string;
    isActive?: boolean;
    mockTestIds?: string[];
  },
): Promise<MockTestBundle> {
  return apiFetch<MockTestBundle>(`/lms/mock-test-bundles/${bundleId}`, { method: "PATCH", json: dto });
}

export async function deleteMockTestBundle(bundleId: string): Promise<void> {
  return apiFetch<void>(`/lms/mock-test-bundles/${bundleId}`, { method: "DELETE" });
}

export async function purchaseMockTestBundle(bundleId: string): Promise<MockTestPurchase[]> {
  return apiFetch<MockTestPurchase[]>(`/lms/mock-test-bundles/${bundleId}/purchase`, { method: "POST" });
}

export async function getMockTestAttemptReview(mockTestId: string, attemptId: string): Promise<MockTestQuestion[]> {
  const data = await apiFetch<MockTestQuestion[]>(`/lms/mock-tests/${mockTestId}/attempts/${attemptId}/review`, { method: "GET" });
  return data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export async function getAdminMockTestAttemptReview(mockTestId: string, attemptId: string): Promise<MockTestQuestion[]> {
  const data = await apiFetch<MockTestQuestion[]>(`/lms/mock-tests/${mockTestId}/admin/attempts/${attemptId}/review`, { method: "GET" });
  return data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/**
 * Generates a short-lived (5 min) signed S3 URL for an audio file.
 * Only works while the exam attempt is active (not submitted, not expired).
 * 
 * @param attemptId - The active exam attempt ID
 * @param s3Key - The S3 object key extracted from the audio URL (e.g. "uploads/lms/audios/xxxx.mp3")
 */
export async function getSignedAudioUrl(
  attemptId: string,
  s3Key: string,
): Promise<{ signedUrl: string; expiresIn: number }> {
  return apiFetch<{ signedUrl: string; expiresIn: number }>(
    `/lms/mock-test-attempts/${attemptId}/audio-token`,
    { method: "POST", json: { s3Key } },
  );
}

export type RegradeResult = {
  dryRun: boolean;
  total: number;
  changed: number;
  summary: Array<{
    attemptId: string;
    userId: number;
    attemptNumber: number;
    submittedAt: string;
    oldScore: number;
    newScore: number;
    oldIsPassed: boolean | null;
    newIsPassed: boolean;
    scoreChanged: boolean;
    passChanged: boolean;
  }>;
};

/**
 * Admin: Re-grade all submitted attempts for a mock test.
 * dryRun=true (default) → preview only, no DB writes.
 * dryRun=false           → apply & commit changes.
 */
export async function regradeAttempts(
  mockTestId: string,
  dryRun: boolean = true,
): Promise<RegradeResult> {
  return apiFetch<RegradeResult>(
    `/lms/mock-tests/${mockTestId}/regrade?dryRun=${dryRun}`,
    { method: "POST" },
  );
}
